import { TFunction } from "i18next";
import _ from "lodash";
import {
  defaultLanguage,
  SupportedLocale,
} from "components/supported-language";
import type { StandardObject } from "utils";
import { ConfigInputTargetValue } from "./config";

export type SchemaComponentType =
  | "emptybox"
  | "dummy"
  | "card"
  | "textfield"
  | "boolean"
  | "datetime"
  | "enum"
  | "imgleftcard"
  | "imgrightcard"
  | "imgtopcard"
  | "tabimgcard"
  | "imgimgcard"
  | "carousel";

export type SchemaComponentCategory = "CONTAINER" | "ITEM" | "DUMMY";
export type DroppableGridAccept =
  | "ITEM"
  | "CONTAINER"
  | "TEMPLATE_ITEM"
  | "TEMPLATE_CONTAINER"
  | "DUMMY";

export type DataSchemaType =
  | "string"
  | "number"
  | "integer"
  | "boolean"
  | "datetime"
  | "array"
  | "file";

export interface DataSchema {
  name: string;
  type: DataSchemaType;
  options?: string[];
  example?: string;
  mandatory?: boolean; // Default false
  description?: string;
}

export interface DisplaySchemaProp {
  [propName: string]: {
    name: string | string[] | null;
    format?: string;
    valueLabel?: StandardObject;
    valueColor?: StandardObject;
    defaultColor?: string;
  };
}

export interface SchemaItem {
  componentType: SchemaComponentType;
  componentProps: StandardObject;
  schemaProps?: DisplaySchemaProp;
  locales?: Record<SupportedLocale, StandardObject>;
}

export type GridSize = "s" | "m" | "l" | "f";
export interface GridItem extends SchemaItem {
  gridSize?: GridSize;
  gridSpan?: number; // To be deprecated
  maxCol?: number; // To be deprecated
  parentKey?: string;
}

export interface GridItemMap {
  [key: string]: GridItem;
}

export interface GridArrItem {
  key: string;
  items?: GridArrItem[];
}
export interface DragItem extends SchemaItem {
  schemaKey?: string;
  draggable: boolean;
}

export interface PortalDisplaySchema {
  lastSavedAt?: number;
  version?: string;
  gridItems: GridItemMap;
  gridArr: GridArrItem[];
  maxCol?: number; // To be deprecated
}

export const getCategoryFromType = (type: SchemaComponentType) => {
  switch (type) {
    case "card":
    case "imgleftcard":
    case "imgrightcard":
    case "imgtopcard":
    case "tabimgcard":
    case "imgimgcard":
    case "carousel":
      return "CONTAINER";
    case "textfield":
    case "boolean":
    case "datetime":
    case "enum":
      return "ITEM";
    case "dummy":
      return "DUMMY";
    default:
      return "DUMMY";
  }
};

export interface ItemParentGridInfo {
  type?: string;
  gridSize?: GridSize;
}

export const getItemGridSizeOptions = ({
  type, // parent component type
  gridSize, // parent gridsize
}: ItemParentGridInfo) => {
  const parentSize = !!gridSize
    ? getGridContainerSize({ gridSize })
    : undefined;
  const getCardOptions = () => {
    if (parentSize !== undefined) {
      if (parentSize["md"] <= 4) {
        return [6, 6, 12, 12];
      } else if (parentSize["md"] <= 6) {
        return [4, 6, 8, 12];
      }
    }
    return [3, 6, 9, 12];
  };
  const getImgCardOptions = () => {
    if (parentSize !== undefined) {
      if (parentSize["md"] >= 8) {
        return [4, 6, 8, 12];
      }
    }
    return [6, 6, 12, 12];
  };
  switch (type) {
    case "imgleftcard":
    case "imgrightcard":
    case "tabimgcard":
      return getImgCardOptions();
    case "imgtopcard":
    case "card":
    default:
      return getCardOptions();
  }
};

export const getGridContainerSize = ({ gridSize }: { gridSize: GridSize }) => {
  const gridSizes = ["s", "m", "l", "f"];
  const mdOptions = [4, 6, 8, 12];
  const xsOptions = [6, 12, 12, 12];
  return {
    xs: xsOptions[gridSizes.indexOf(gridSize)],
    md: mdOptions[gridSizes.indexOf(gridSize)],
  };
};

export const getGridItemSize = ({
  gridSize,
  parent,
}: {
  gridSize: GridSize;
  parent: ItemParentGridInfo;
}) => {
  const gridSizes = ["s", "m", "l", "f"];
  const options = getItemGridSizeOptions(parent);
  return {
    xs: options[gridSizes.indexOf(gridSize)],
  };
};

export const checkHasItem = (type: SchemaComponentType) => {
  switch (type) {
    case "imgimgcard":
      return false;
    default:
      return getCategoryFromType(type) === "CONTAINER";
  }
};

export const calculateNewComponentGridCountAndSize = (
  gridArr: GridArrItem[],
  gridItems: GridItemMap,
  pMaxCol: number,
  parent?: {
    type?: SchemaComponentType;
    gridSize?: GridSize;
  }
) => {
  const maxCol = Number(pMaxCol);
  let lastItmSize;
  if (parent === undefined) {
    lastItmSize = 6;
  } else {
    lastItmSize = getGridItemSize({
      gridSize: "s",
      parent: {
        type: parent.type,
        gridSize: parent.gridSize,
      },
    })["xs"];
  }
  const reminder = gridArr.reduce((acc, itm) => {
    if (!gridItems[itm.key]) {
      return acc;
    }
    const gridItm = gridItems[itm.key];
    const pKey = gridItm.parentKey;
    let size;
    if (!!gridItm.gridSize) {
      size = !pKey
        ? getGridContainerSize({ gridSize: gridItm.gridSize })["md"]
        : getGridItemSize({
            gridSize: gridItm.gridSize,
            parent: {
              type: gridItems[pKey].componentType,
              gridSize: gridItems[pKey].gridSize,
            },
          })["xs"];
    } else {
      size = (gridItm.gridSpan ?? 1) * (12 / maxCol);
    }
    lastItmSize = size;
    let newAcc = acc + size;
    if (newAcc > 12) {
      newAcc = size;
    } else if (newAcc === 12) {
      newAcc = 0;
    }
    return newAcc;
  }, 0);
  // When reminder = 0, or in case last grid item has grid span >= maxCol
  const count =
    reminder === 0 || reminder >= 12
      ? Math.floor(12 / lastItmSize)
      : Math.max(Math.floor((12 - reminder) / lastItmSize), 1);
  return {
    count,
    size: lastItmSize,
  };
};

export const getDefaultComponentPropsFromType = (
  type: SchemaComponentType
): StandardObject => {
  switch (type) {
    case "card":
    case "imgleftcard":
    case "imgrightcard":
    case "imgtopcard":
    case "imgimgcard":
      return {
        cardProps: {},
        cardHeaderProps: {},
      };
    case "carousel":
    case "tabimgcard":
      return {
        cardProps: {},
        cardHeaderProps: {},
        tabProps: {},
        tabImgProps: {},
      };
    case "enum":
      return {
        textfieldProps: {},
        chipProps: {},
      };
    default:
      return {};
  }
};

export const getDefaultSchemaFromType = (
  type: SchemaComponentType
): DisplaySchemaProp => {
  switch (type) {
    case "textfield":
    case "boolean":
    case "imgleftcard":
    case "imgrightcard":
    case "imgtopcard":
      return {
        value: { name: null },
      };
    case "tabimgcard":
    case "carousel":
    case "imgimgcard":
      return {
        value: {
          name: [],
          valueLabel: {},
        },
      };
    case "datetime":
      return {
        value: {
          name: null,
        },
      };
    case "enum":
      return {
        value: {
          name: null,
          valueLabel: {},
          valueColor: {},
          defaultColor: "default",
        },
      };
    default:
      return {};
  }
};

export const getRequiredFieldsFromType = (
  type: SchemaComponentType,
  locales?: SchemaItem["locales"]
): string[] => {
  const ret: string[] = [];
  switch (type) {
    case "card":
      ret.push("componentProps.cardHeaderProps.title");
      if (!!locales) {
        Object.keys(locales).forEach((locale) => {
          ret.push(`locales.${locale}.componentProps.cardHeaderProps.title`);
        });
      }
      break;
    case "imgleftcard":
    case "imgrightcard":
    case "imgtopcard":
      ret.push(
        "componentProps.cardHeaderProps.title",
        "schemaProps.value.name"
      );
      if (!!locales) {
        Object.keys(locales).forEach((locale) => {
          ret.push(`locales.${locale}.componentProps.cardHeaderProps.title`);
        });
      }
      break;
    case "tabimgcard":
    case "carousel":
    case "imgimgcard":
      ret.push(
        "componentProps.cardHeaderProps.title",
        "schemaProps.value.name",
        "schemaProps.value.valueLabel"
      );
      if (!!locales) {
        Object.keys(locales).forEach((locale) => {
          ret.push(`locales.${locale}.componentProps.cardHeaderProps.title`);
        });
      }
      break;

    case "textfield":
    case "datetime":
      ret.push("componentProps.label", "schemaProps.value.name");
      if (!!locales) {
        Object.keys(locales).forEach((locale) => {
          ret.push(`locales.${locale}.componentProps.label`);
        });
      }
      break;
    case "boolean":
      ret.push("componentProps.title", "schemaProps.value.name");
      if (!!locales) {
        Object.keys(locales).forEach((locale) => {
          ret.push(`locales.${locale}.componentProps.title`);
        });
      }
      break;
    case "enum":
      ret.push("componentProps.textfieldProps.label", "schemaProps.value.name");
      if (!!locales) {
        Object.keys(locales).forEach((locale) => {
          ret.push(`locales.${locale}.componentProps.textfieldProps.label`);
        });
      }
      break;
    default:
      break;
  }
  return ret;
};

export const checkNestedObjHasValue = (
  key: string,
  obj: StandardObject | undefined
) => {
  const keys = key.split(".");
  if (obj === undefined) {
    return false;
  }
  let ret = true;
  let j = obj;
  keys.forEach((k) => {
    if (!j[k]) {
      ret = false;
    }
    if (ret) {
      j = j[k];
    }
  });
  return ret;
};

export const getValueFromNestedObj: (
  obj: StandardObject | undefined,
  key: string
) => StandardObject | ConfigInputTargetValue | undefined = (
  obj: StandardObject | undefined,
  key: string
) => {
  const keys = key.split(".");
  const ret = keys.reduce((acc, k) => {
    if (acc === undefined) {
      return undefined;
    }
    return acc[k];
  }, obj);
  return ret;
};

export const getValueAndOnChangeKey = (
  obj: StandardObject | undefined,
  curLocale: SupportedLocale,
  key: string
) => {
  if (curLocale !== defaultLanguage) {
    return {
      value: getValueFromNestedObj(obj, `locales.${curLocale}.${key}`),
      onChangeKey: `locales.${curLocale}.${key}`,
    };
  } else {
    return {
      value: getValueFromNestedObj(obj, key),
      onChangeKey: key,
    };
  }
};

export const getHasMissingProps = (
  componentType: SchemaComponentType,
  componentProps: StandardObject,
  schemaProps?: DisplaySchemaProp,
  locales?: SchemaItem["locales"]
) => {
  const requiredProps = getRequiredFieldsFromType(componentType, locales);
  const hasMissingProps = requiredProps.some((prop) => {
    const ret =
      (prop.startsWith("componentProps") &&
        !checkNestedObjHasValue(
          prop.replace("componentProps.", ""),
          componentProps
        )) ||
      (prop.startsWith("locales") &&
        (!locales ||
          !checkNestedObjHasValue(prop.replace("locales.", ""), locales))) ||
      (prop.startsWith("schemaProps") &&
        (!schemaProps ||
          !checkNestedObjHasValue(
            prop.replace("schemaProps.", ""),
            schemaProps
          )));
    return ret;
  });
  return hasMissingProps;
};

export interface PortalDisplaySchemaErrorMap {
  [key: string]: string[];
}

const getFieldNameFromField = (field: string, t: TFunction<"application">) => {
  const fieldName = field.split(".").pop() as "title" | "label" | "name";
  return t(`portalDisplaySchema.fields.${fieldName}`);
};

export const checkPortalDisplaySchemaMapForError = (
  schema: PortalDisplaySchema,
  t: TFunction<"application">
) => {
  const gridItems = schema.gridItems;
  const errorMap: PortalDisplaySchemaErrorMap = {};
  Object.keys(gridItems).forEach((key) => {
    const item = gridItems[key];
    const type = item.componentType;
    const pkey = item.parentKey;
    // Check hidden child in tabimgcard
    if (!!pkey) {
      const parentItem = gridItems[pkey];
      if (
        !!parentItem &&
        parentItem.componentType === "tabimgcard" &&
        parentItem.componentProps.tabImgProps?.itemGridSize === "nofield"
      ) {
        if (!errorMap[pkey]) {
          errorMap[pkey] = [];
        }
        errorMap[pkey].push(t("portalDisplaySchema.errors.ITEMSPACE_NOT_NULL"));
      }
    }
    const requiredFields = getRequiredFieldsFromType(type, item.locales);
    requiredFields.forEach((field) => {
      if (!checkNestedObjHasValue(field, item)) {
        // Has Error
        if (process.env.NODE_ENV === "development") {
          console.log("Error in portal schema:", key, field);
        }
        if (!errorMap[key]) {
          errorMap[key] = [];
        }
        errorMap[key].push(
          t("portalDisplaySchema.errors.MISSING_REQUIRED", {
            field: getFieldNameFromField(field, t),
          })
        );
        if (!!pkey) {
          if (!errorMap[pkey]) {
            errorMap[pkey] = [];
          }
          errorMap[pkey].push(
            t("portalDisplaySchema.errors.MISSING_CHILD_REQUIRED", {
              field: getFieldNameFromField(field, t),
            })
          );
        }
      }
    });
  });
  return errorMap;
};

export const checkHasDeprecatedFields = (schema: PortalDisplaySchema) => {
  if (!!schema.maxCol) {
    return true;
  }
  Object.values(schema.gridItems).forEach((item) => {
    if (!!item.gridSpan || !!item.maxCol) {
      return true;
    }
  });
  return false;
};

const getGridSizeFromMaxColAndSpan = (maxCol: number, gridSpan: number) => {
  if (!!gridSpan) {
    switch (maxCol) {
      case 1:
        return "f";
      case 2:
        return gridSpan > 1 ? "f" : "m";
      case 3:
        if (gridSpan > 2) {
          return "f";
        } else if (gridSpan > 1) {
          return "l";
        } else {
          return "s";
        }
      case 4:
        if (gridSpan > 3) {
          return "f";
        } else if (gridSpan > 2) {
          return "l";
        } else if (gridSpan > 1) {
          return "m";
        } else {
          return "s";
        }
      default:
        return "m";
    }
  }
};

export const updateDeprecatedFields = (
  schema: PortalDisplaySchema
): PortalDisplaySchema => {
  const newSchema = _.cloneDeep(schema);
  const { gridItems, gridArr } = newSchema;
  const isOldMaxColNumber =
    !!newSchema.maxCol && !isNaN(Number(newSchema.maxCol));
  const oldMaxCol = isOldMaxColNumber ? Number(newSchema.maxCol) : 0;
  gridArr.forEach((itm) => {
    // Set gridSize for container
    const gridItm = gridItems[itm.key];
    if (!gridItm.gridSize) {
      const gridSpan = Number(gridItm.gridSpan);
      const size = getGridSizeFromMaxColAndSpan(oldMaxCol, gridSpan);
      gridItm.gridSize = size;
    }
    // Set for items
    const parentMaxCol = gridItm.maxCol ?? 1;
    if (!!itm.items) {
      itm.items.forEach((subItm) => {
        const subGridItm = gridItems[subItm.key];
        if (!subGridItm.gridSize) {
          const subGridSpan = Number(subGridItm.gridSpan);
          const subSize = getGridSizeFromMaxColAndSpan(
            parentMaxCol,
            subGridSpan
          );
          subGridItm.gridSize = subSize;
        }
        if (subGridItm.gridSpan) {
          delete subGridItm.gridSpan;
        }
      });
    }
    if (gridItm.gridSpan) {
      delete gridItm.gridSpan;
    }
    delete gridItm.maxCol;
  });
  newSchema.maxCol = undefined;
  return newSchema;
};
